/*
Fork addition: in-game Captains of Industry blueprint string ->
planner plan converter (the reverse direction of the blueprint pack,
which authors in-game layouts). This module is pure (no Vue, no DOM
beyond TextDecoder) so it can be bundled with esbuild and verified
headlessly in node — see tools/coi-import-verify.mjs.

In-game format (port of the verified Python reference,
~/workspace/custom-skills/local-coi-blueprints/references/coi_bp.py):
  B<base64len>:<base64(gzip(blob))>   ('F' = folder of blueprints)
Binary blob (Mafi BlobReader): UInt = LEB128, Int = zig-zag UInt,
Bool = 1 byte, Bytes = UInt len + raw, String = interned (table
starts [None]; ref == len(table) reads a new UInt utf8-len + bytes).
Header: libraryVersion UInt; if version == 7 one extra UInt;
gameVersion String; saveVersion UInt; name String; description
String; entityCount UInt; entities; tail bytes (ignored).
Each entity is five typed dicts in order: ints, bools, strings,
strArrays, byteArrays — see decodeCoiBlueprint below.

Planner format: SavedBlueprint with SHORT keys everywhere (item,
recipe and link-product names) — the catalog handed to
convertCoiBlueprint works in FULL-name space and translates via
catalog.keyOf at save time.
*/
import {toUint8Array} from 'js-base64';
import {ungzip} from 'pako';
import type {SavedBlueprint, SavedItem, SavedLink} from './model/saved-blueprint';
import {Objective} from './types';

/* ------------------------------------------------------------------ */
/* Catalog (full-name space)                                          */
/* ------------------------------------------------------------------ */

export interface CoiCatalogRecipe {
    name: string;
    inputs: string[];
    outputs: string[];
}

export interface CoiCatalog {
    factories: Map<string, {recipes: CoiCatalogRecipe[]}>;
    keyOf(fullName: string): string | undefined;
    //GameItemType number for a product (full name); a product is
    //linkable stock iff this is neither Special (9) nor Energy (8)
    productType(fullName: string): number | undefined;
    defaultRecipeOf(itemFullName: string): CoiCatalogRecipe | undefined;
}

export interface CoiImportStats {
    totalEntities: number;
    cardsPlaced: number;
    transportsSkipped: number;
    storagesSkipped: number;
    unknownSkipped: number;
    recipesExact: number;
    recipesNormalized: number;
    recipesPrefix: number;
    recipesToken: number;
    recipesTokenSubset: number;
    recipesDefaulted: number;
    multiOption: number;
    linksCreated: number;
    linksGuessed: number;
    reflectedPlaced: number;
}

export type CoiImportResult =
    | {ok: true; savedBlueprint: SavedBlueprint; name?: string; notes: string[]; stats: CoiImportStats}
    | {ok: false; error: string};

/* ------------------------------------------------------------------ */
/* Extraction from pasted text                                        */
/* ------------------------------------------------------------------ */

const TOKEN_START = /([BF])(\d+):/;
const BASE64_RUN = /^[A-Za-z0-9+/=]+/;
const BASE64_LINE = /^[A-Za-z0-9+/=]+$/;

//Finds a `[BF]<digits>:` in-game blueprint token anywhere in pasted
//text (surrounding [code]/``` wrappers, ====Name==== headers,
//whitespace, line-wrapped payloads and surrounding prose are all
//tolerated) and returns the kind plus the base64 payload with ALL
//whitespace removed. Planner strings (DAXFB... / raw JSON) contain
//no such token and return undefined.
export function extractCoiBlueprintString(text: string): {kind: 'B' | 'F'; payload: string} | undefined {
    const match = TOKEN_START.exec(text);
    if(!match)
        return undefined;
    const kind = match[1] as 'B' | 'F';
    const rest = text.substring(match.index + match[0].length);
    const lines = rest.split(/\r?\n/);
    let payload = '';
    for(let i = 0; i < lines.length; ++i) {
        const line = lines[i]!.trim();
        if(i == 0) {
            const run = BASE64_RUN.exec(line);
            if(!run)
                return undefined;
            payload = run[0];
            if(run[0].length < line.length)
                break; //junk right after the token on the same line
        } else {
            if(!BASE64_LINE.test(line))
                break;
            payload += line;
        }
    }
    return payload ? {kind, payload} : undefined;
}

/* ------------------------------------------------------------------ */
/* Blob decoding (faithful port of coi_bp.py)                         */
/* ------------------------------------------------------------------ */

export interface CoiTransform {
    x: number;
    y: number;
    z: number;
    rotation: number;
    reflected: boolean;
}

export interface CoiTrajectory {
    startDir: [number, number, number];
    endDir: [number, number, number];
    pivots: [number, number, number][];
}

export interface CoiEntity {
    ints: Record<string, number>;
    bools: Record<string, boolean>;
    strings: Record<string, string>;
    strArrays: Record<string, string[]>;
    byteArrays: Record<string, Uint8Array>;
    transform?: CoiTransform;
    trajectory?: CoiTrajectory;
}

export interface CoiDecodedBlueprint {
    kind: 'B' | 'F';
    libraryVersion: number;
    headerExtra?: number;
    gameVersion?: string;
    saveVersion?: number;
    name?: string;
    description?: string;
    entities: CoiEntity[];
}

class BlobReader {
    private readonly _data: Uint8Array;
    private _pos = 0;
    private readonly _strings: (string | undefined)[] = [undefined];

    constructor(data: Uint8Array) {
        this._data = data;
    }

    u(): number {
        let result = 0;
        let shift = 0;
        for(;;) {
            if(this._pos >= this._data.length)
                throw new Error('unexpected end of blob');
            const byte = this._data[this._pos++]!;
            result += (byte & 0x7f) * (2 ** shift);
            if(!(byte & 0x80))
                return result;
            shift += 7;
            if(shift > 63)
                throw new Error('UInt too large');
        }
    }

    i(): number {
        const n = this.u();
        return (n % 2 == 0) ? n / 2 : -(n + 1) / 2;
    }

    bool(): boolean {
        if(this._pos >= this._data.length)
            throw new Error('unexpected end of blob');
        const v = this._data[this._pos++]!;
        if(v !== 0 && v !== 1)
            throw new Error(`bad bool byte ${v}`);
        return v == 1;
    }

    bytes(): Uint8Array {
        const len = this.u();
        if(this._pos + len > this._data.length)
            throw new Error('unexpected end of blob');
        const out = this._data.subarray(this._pos, this._pos + len);
        this._pos += len;
        return out;
    }

    s(): string | undefined {
        const ref = this.u();
        if(ref == this._strings.length) {
            const len = this.u();
            if(this._pos + len > this._data.length)
                throw new Error('unexpected end of blob');
            const text = new TextDecoder().decode(this._data.subarray(this._pos, this._pos + len));
            this._pos += len;
            this._strings.push(text);
            return text;
        }
        if(ref > this._strings.length)
            throw new Error(`string ref ${ref} out of range`);
        return this._strings[ref];
    }
}

function decodeTransform(blob: Uint8Array): CoiTransform {
    const r = new BlobReader(blob);
    return {x: r.i(), y: r.i(), z: r.i(), rotation: r.i(), reflected: r.bool()};
}

function decodeTrajectory(blob: Uint8Array): CoiTrajectory {
    const r = new BlobReader(blob);
    const startDir: [number, number, number] = [r.i(), r.i(), r.i()];
    const endDir: [number, number, number] = [r.i(), r.i(), r.i()];
    const count = r.u();
    const pivots: [number, number, number][] = [];
    for(let k = 0; k < count; ++k)
        pivots.push([r.i(), r.i(), r.i()]);
    return {startDir, endDir, pivots};
}

function readEntity(r: BlobReader): CoiEntity {
    const entity: CoiEntity = {ints: {}, bools: {}, strings: {}, strArrays: {}, byteArrays: {}};
    const intCount = r.u();
    for(let k = 0; k < intCount; ++k) {
        const key = r.s()!;
        entity.ints[key] = r.i();
    }
    const boolCount = r.u();
    for(let k = 0; k < boolCount; ++k) {
        const key = r.s()!;
        entity.bools[key] = r.bool();
    }
    const stringCount = r.u();
    for(let k = 0; k < stringCount; ++k) {
        const key = r.s()!;
        entity.strings[key] = r.s()!;
    }
    const strArrayCount = r.u();
    for(let k = 0; k < strArrayCount; ++k) {
        const key = r.s()!;
        const len = r.u();
        const arr: string[] = [];
        for(let j = 0; j < len; ++j)
            arr.push(r.s()!);
        entity.strArrays[key] = arr;
    }
    const byteArrayCount = r.u();
    for(let k = 0; k < byteArrayCount; ++k) {
        const key = r.s()!;
        entity.byteArrays[key] = r.bytes();
    }
    const transform = entity.byteArrays['Transform'];
    if(transform)
        entity.transform = decodeTransform(transform);
    const trajectory = entity.byteArrays['Trajectory'];
    if(trajectory)
        entity.trajectory = decodeTrajectory(trajectory);
    return entity;
}

const OUTER = /^([BF])(\d+):([\s\S]+)$/;

//Decodes a full `B…:…` / `F…:…` token. Throws on any corruption.
//The length prefix is NOT enforced (Hub copies can be sloppy) —
//decoding itself is the test, exactly as convertCoiBlueprint treats
//it. Exposed for the headless parity harness; the UI entry point is
//convertCoiBlueprint.
export function decodeCoiBlueprint(payload: string): CoiDecodedBlueprint {
    const match = OUTER.exec(payload.trim());
    if(!match)
        throw new Error('not a blueprint/folder string');
    const kind = match[1] as 'B' | 'F';
    const base64 = match[3]!.replace(/\s+/g, '');
    const compressed = toUint8Array(base64);
    const blob = ungzip(compressed);
    const r = new BlobReader(blob);
    const libraryVersion = r.u();
    const out: CoiDecodedBlueprint = {kind, libraryVersion, entities: []};
    if(libraryVersion == 7)
        out.headerExtra = r.u();
    out.gameVersion = r.s();
    out.saveVersion = r.u();
    out.name = r.s();
    out.description = r.s();
    const entityCount = r.u();
    for(let k = 0; k < entityCount; ++k)
        out.entities.push(readEntity(r));
    //tail bytes are ignored, as in the Python reference
    return out;
}

/* ------------------------------------------------------------------ */
/* Recipe matching                                                    */
/* ------------------------------------------------------------------ */

function normalizeRecipeName(name: string): string {
    return name.toLowerCase().replace(/(rt|t)\d+$/, '');
}

//camelCase tokens, keeping a trailing digit run attached to its word
//(so 'Parts1' stays one token and 'T4' can be dropped as a tier token)
function recipeTokens(name: string): string[] {
    const chunks = name.split(/(?<=[a-z0-9])(?=[A-Z])|(?<=[A-Z])(?=[A-Z][a-z])/).filter(Boolean);
    return chunks.filter((token) => !/^(rt|t)\d+$/i.test(token));
}

function multisetEqual(a: string[], b: string[]): boolean {
    if(a.length != b.length)
        return false;
    const counts = new Map<string, number>();
    for(const t of a)
        counts.set(t, (counts.get(t) || 0) + 1);
    for(const t of b) {
        const c = counts.get(t);
        if(!c)
            return false;
        counts.set(t, c - 1);
    }
    return true;
}

function multisetSubset(a: string[], b: string[]): boolean {
    const counts = new Map<string, number>();
    for(const t of b)
        counts.set(t, (counts.get(t) || 0) + 1);
    for(const t of a) {
        const c = counts.get(t) || 0;
        if(c <= 0)
            return false;
        counts.set(t, c - 1);
    }
    return true;
}

type MatchTier = 100 | 90 | 85 | 70 | 40 | 0;

function matchTier(gameId: string, candidateName: string, itemName: string): MatchTier {
    if(gameId == candidateName)
        return 100;
    const gameNorm = normalizeRecipeName(gameId);
    const candNorm = normalizeRecipeName(candidateName);
    if(gameNorm == candNorm)
        return 90;
    //building-prefix variants, then normalized equality
    if(candidateName.startsWith(itemName) && normalizeRecipeName(candidateName.substring(itemName.length)) == gameNorm)
        return 85;
    if(normalizeRecipeName(itemName + gameId) == candNorm)
        return 85;
    const gameTokens = recipeTokens(gameId);
    const candTokens = recipeTokens(candidateName);
    if(multisetEqual(gameTokens, candTokens))
        return 70;
    if(multisetSubset(gameTokens, candTokens) || multisetSubset(candTokens, gameTokens))
        return 40;
    return 0;
}

function isConcrete(name: string): boolean {
    return !name.startsWith('Any');
}

function isStock(catalog: CoiCatalog, productFullName: string): boolean {
    const type = catalog.productType(productFullName);
    return type !== undefined && type !== 8 && type !== 9;
}

/* ------------------------------------------------------------------ */
/* Classification helpers                                             */
/* ------------------------------------------------------------------ */

function isTransportPrototype(prototype: string): boolean {
    return prototype.includes('Conveyor')
        || prototype.startsWith('PipeT')
        || prototype.includes('Zipper')
        || prototype.includes('MiniZip')
        || prototype.includes('Lift')
        || prototype.includes('MoltenMetalChannel')
        || prototype.includes('Sorter')
        || prototype.includes('Flywheel')
        || prototype.includes('PipeProductsSource');
}

function isConnectorStatic(prototype: string): boolean {
    return prototype.startsWith('Zipper')
        || prototype.startsWith('Lift')
        || prototype.startsWith('MiniZip')
        || prototype.includes('Sorter');
}

const ORIENTATION_BY_ROTATION: Record<number, 'TB' | 'RL' | 'BT'> = {1: 'TB', 2: 'RL', 3: 'BT'};

/* ------------------------------------------------------------------ */
/* Union-find for belt-connection evidence                            */
/* ------------------------------------------------------------------ */

class UnionFind {
    private readonly _parent: number[];
    constructor(size: number) {
        this._parent = Array.from({length: size}, (_, i) => i);
    }
    find(x: number): number {
        let root = x;
        while(this._parent[root] != root)
            root = this._parent[root]!;
        while(this._parent[x] != root) {
            const next = this._parent[x]!;
            this._parent[x] = root;
            x = next;
        }
        return root;
    }
    union(a: number, b: number) {
        const ra = this.find(a);
        const rb = this.find(b);
        if(ra != rb)
            this._parent[rb] = ra;
    }
}

type Point3 = readonly [number, number, number];

function distance3(a: Point3, b: Point3): number {
    const dx = a[0] - b[0];
    const dy = a[1] - b[1];
    const dz = a[2] - b[2];
    return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/* ------------------------------------------------------------------ */
/* Conversion                                                         */
/* ------------------------------------------------------------------ */

const SUPPORTED_LIBRARY_VERSIONS = new Set([4, 6, 7]);
const CARD_WIDTH = 300;

interface CardDraft {
    entityIndex: number;
    prototype: string;
    recipe: CoiCatalogRecipe;
    tile: Point3;
    rotation: number;
    reflected: boolean;
    x: number;
    y: number;
    height: number;
}

export function convertCoiBlueprint(payload: string, catalog: CoiCatalog): CoiImportResult {
    const trimmed = payload.trim();
    const kindMatch = /^([BF])\d+:/.exec(trimmed);
    if(kindMatch && kindMatch[1] == 'F') {
        return {ok: false, error: 'This is a blueprint folder (it contains several blueprints). Paste a single blueprint string instead.'};
    }
    let decoded: CoiDecodedBlueprint;
    try {
        decoded = decodeCoiBlueprint(trimmed);
    } catch(err) {
        const message = err instanceof Error ? err.message : String(err);
        return {ok: false, error: `This looks like a Captains of Industry blueprint, but it could not be decoded (${message})`};
    }
    if(decoded.kind == 'F') {
        return {ok: false, error: 'This is a blueprint folder (it contains several blueprints). Paste a single blueprint string instead.'};
    }
    if(!SUPPORTED_LIBRARY_VERSIONS.has(decoded.libraryVersion)) {
        return {ok: false, error: `This blueprint uses library version ${decoded.libraryVersion}, which the converter does not support (supported: 4, 6, 7).`};
    }

    const stats: CoiImportStats = {
        totalEntities: decoded.entities.length,
        cardsPlaced: 0,
        transportsSkipped: 0,
        storagesSkipped: 0,
        unknownSkipped: 0,
        recipesExact: 0,
        recipesNormalized: 0,
        recipesPrefix: 0,
        recipesToken: 0,
        recipesTokenSubset: 0,
        recipesDefaulted: 0,
        multiOption: 0,
        linksCreated: 0,
        linksGuessed: 0,
        reflectedPlaced: 0,
    };

    //blueprint-wide stored-products evidence (for recipe bonuses)
    const storedProducts = new Set<string>();
    for(const entity of decoded.entities) {
        const stored = entity.strings['StorageStoredProduct'];
        if(stored)
            storedProducts.add(stored.startsWith('Product_') ? stored.substring('Product_'.length) : stored);
    }
    //catalog product names (for the material-variant bonus)
    const productNames = new Set<string>();
    for(const {recipes} of catalog.factories.values()) {
        for(const recipe of recipes) {
            for(const name of recipe.inputs)
                productNames.add(name);
            for(const name of recipe.outputs)
                productNames.add(name);
        }
    }

    const unknownNames: string[] = [];
    const defaultedIds: string[] = [];
    const cards: CardDraft[] = [];
    //entity index -> anchor node id (assigned below, in entity order
    //for every entity that carries a Transform)
    const isCardByEntity = new Set<number>();
    const isStorageByEntity = new Set<number>();

    decoded.entities.forEach((entity, entityIndex) => {
        const prototype = entity.strings['Prototype'];
        const hasStoredProduct = entity.strings['StorageStoredProduct'] !== undefined;
        const factory = prototype ? catalog.factories.get(prototype) : undefined;

        //storage-like: no card
        const allAbstractOnly = !!factory && factory.recipes.length > 0
            && factory.recipes.every((r) => [...r.inputs, ...r.outputs].every((n) => !isConcrete(n)));
        if((prototype && prototype.startsWith('Storage')) || hasStoredProduct || allAbstractOnly) {
            stats.storagesSkipped++;
            isStorageByEntity.add(entityIndex);
            return;
        }
        //transport-like: no card
        if(entity.trajectory || (prototype && isTransportPrototype(prototype))) {
            stats.transportsSkipped++;
            return;
        }
        //a real factory with at least one concrete recipe -> card
        if(prototype && factory && factory.recipes.some((r) => [...r.inputs, ...r.outputs].some(isConcrete))) {
            isCardByEntity.add(entityIndex);
            const chosen = chooseRecipe(prototype, factory.recipes, entity.strArrays['Recipes'] || [], catalog, storedProducts, productNames, stats, defaultedIds);
            const transform = entity.transform;
            const tile: Point3 = transform ? [transform.x, transform.y, transform.z] : [0, 0, 0];
            const concreteIoCount = [...chosen.inputs, ...chosen.outputs].filter(isConcrete).length;
            const height = Math.min(360, Math.max(170, 120 + 26 * concreteIoCount));
            cards.push({
                entityIndex,
                prototype,
                recipe: chosen,
                tile,
                rotation: transform?.rotation ?? 0,
                reflected: !!transform?.reflected,
                x: Math.round((tile[0] * 75) / 25) * 25,
                y: Math.round((tile[1] * 75) / 25) * 25,
                height,
            });
            if(transform?.reflected)
                stats.reflectedPlaced++;
            return;
        }
        //anything else: unknown prototype
        stats.unknownSkipped++;
        if(prototype && !unknownNames.includes(prototype))
            unknownNames.push(prototype);
    });

    stats.cardsPlaced = cards.length;

    if(!cards.length) {
        return {ok: false, error: `This blueprint decoded, but none of its buildings exist in the planner's data (game v0.8.1a), so there is nothing to import.`};
    }

    //deterministic de-overlap: process in (x, then y) order, shift
    //down by one grid step while the padded rect overlaps a placed one
    const placed: {x: number; y: number; w: number; h: number}[] = [];
    const sortedCards = [...cards].sort((a, b) => (a.x - b.x) || (a.y - b.y));
    for(const card of sortedCards) {
        for(;;) {
            const overlaps = placed.some((p) => rectsOverlap(card.x - 10, card.y - 10, CARD_WIDTH + 20, card.height + 20, p.x, p.y, p.w, p.h));
            if(!overlaps)
                break;
            card.y += 25;
        }
        placed.push({x: card.x - 10, y: card.y - 10, w: CARD_WIDTH + 20, h: card.height + 20});
    }

    /* ---- belt-connection evidence (union-find) ---- */
    //anchor nodes: every entity with a Transform, in entity order
    const anchorNodeByEntity = new Map<number, number>();
    const anchorPoints: Point3[] = [];
    decoded.entities.forEach((entity, entityIndex) => {
        if(entity.transform) {
            anchorNodeByEntity.set(entityIndex, anchorPoints.length);
            anchorPoints.push([entity.transform.x, entity.transform.y, entity.transform.z]);
        }
    });
    const transportEnds: {point: Point3; node: number}[] = [];
    const uf = new UnionFind(anchorPoints.length + decoded.entities.length * 2);
    let endNodeCount = anchorPoints.length;
    decoded.entities.forEach((entity) => {
        if(!entity.trajectory || !entity.trajectory.pivots.length)
            return;
        const pivots = entity.trajectory.pivots;
        const first = pivots[0]!;
        const last = pivots[pivots.length - 1]!;
        const firstNode = endNodeCount++;
        const lastNode = endNodeCount++;
        transportEnds.push({point: first, node: firstNode}, {point: last, node: lastNode});
        //(a) the two ends of each transport are connected
        uf.union(firstNode, lastNode);
    });
    //(b) each transport end joins its nearest anchor within 7 tiles
    for(const end of transportEnds) {
        let best = -1;
        let bestDistance = Infinity;
        anchorPoints.forEach((point, idx) => {
            const d = distance3(end.point, point);
            if(d < bestDistance) {
                bestDistance = d;
                best = idx;
            }
        });
        if(best >= 0 && bestDistance <= 7)
            uf.union(end.node, best);
    }
    //(c) connector statics join the nearest card-or-storage anchor
    decoded.entities.forEach((entity, entityIndex) => {
        const prototype = entity.strings['Prototype'] || '';
        if(!isConnectorStatic(prototype))
            return;
        const node = anchorNodeByEntity.get(entityIndex);
        if(node === undefined)
            return;
        let best = -1;
        let bestDistance = Infinity;
        for(const [otherIndex, otherNode] of anchorNodeByEntity) {
            if(otherIndex == entityIndex)
                continue;
            if(!isCardByEntity.has(otherIndex) && !isStorageByEntity.has(otherIndex))
                continue;
            const d = distance3(anchorPoints[node]!, anchorPoints[otherNode]!);
            if(d < bestDistance) {
                bestDistance = d;
                best = otherNode;
            }
        }
        if(best >= 0 && bestDistance <= 7)
            uf.union(node, best);
    });

    const beltConnected = (a: CardDraft, b: CardDraft): boolean => {
        const na = anchorNodeByEntity.get(a.entityIndex);
        const nb = anchorNodeByEntity.get(b.entityIndex);
        if(na === undefined || nb === undefined)
            return false;
        return uf.find(na) == uf.find(nb);
    };

    /* ---- links ---- */
    const links: SavedLink[] = [];
    const hasOutgoing = new Set<number>();
    cards.forEach((consumer, consumerIdx) => {
        const seenProducts = new Set<string>();
        for(const product of consumer.recipe.inputs) {
            if(!isConcrete(product) || seenProducts.has(product))
                continue;
            seenProducts.add(product);
            if(!isStock(catalog, product))
                continue;
            const producers: number[] = [];
            cards.forEach((producer, producerIdx) => {
                if(producerIdx == consumerIdx)
                    return;
                if(producer.recipe.outputs.some((o) => o == product && isConcrete(o)))
                    producers.push(producerIdx);
            });
            if(!producers.length)
                continue;
            let chosenProducers: number[];
            if(producers.length == 1) {
                chosenProducers = producers;
            } else {
                const belted = producers.filter((idx) => beltConnected(cards[idx]!, consumer));
                if(belted.length) {
                    chosenProducers = belted;
                } else {
                    let best = producers[0]!;
                    let bestDistance = Infinity;
                    for(const idx of producers) {
                        const d = distance3(cards[idx]!.tile, consumer.tile);
                        if(d < bestDistance) {
                            bestDistance = d;
                            best = idx;
                        }
                    }
                    chosenProducers = [best];
                    stats.linksGuessed++;
                }
            }
            for(const producerIdx of chosenProducers) {
                const productKey = catalog.keyOf(product);
                const link: SavedLink = {l: [consumerIdx, producerIdx]};
                if(productKey)
                    link.n = productKey;
                links.push(link);
                hasOutgoing.add(producerIdx);
            }
        }
    });
    stats.linksCreated = links.length;

    /* ---- saved items ---- */
    //fork addition (objectives experiment, see tools/coi-import-verify.mjs):
    //graph-sink cards (no outgoing stock links; isolated cards count
    //as sinks) carry the Primary objective so the solver pulls the
    //whole chain to a full count of 1 per card.
    const items: SavedItem[] = cards.map((card, idx) => {
        const itemKey = catalog.keyOf(card.prototype);
        const recipeKey = catalog.keyOf(card.recipe.name);
        const item: SavedItem = {
            n: itemKey ?? card.prototype,
            p: [card.x, card.y],
            r: recipeKey ?? card.recipe.name,
        };
        const orientation = ORIENTATION_BY_ROTATION[card.rotation];
        if(orientation)
            item.or = orientation;
        if(!hasOutgoing.has(idx))
            item.o = Objective.Primary;
        return item;
    });

    const savedBlueprint: SavedBlueprint = {
        h: {g: 'CI', v: 8},
        i: items,
        l: links,
    };

    /* ---- notes ---- */
    const name = decoded.name || undefined;
    const notes: string[] = [];
    notes.push(`Imported "${name ?? 'Untitled blueprint'}": ${cards.length} factories, ${links.length} links.`);
    if(stats.linksGuessed > 0)
        notes.push(`Nearest-guess links: ${stats.linksGuessed} (no belt connection found, linked to the closest producer).`);
    if(stats.transportsSkipped > 0)
        notes.push(`Transport pieces skipped: ${stats.transportsSkipped}.`);
    if(stats.storagesSkipped > 0)
        notes.push(`Storage units not placed: ${stats.storagesSkipped} (their stored products were used as evidence for recipe choices and links).`);
    if(stats.recipesDefaulted > 0) {
        const named = defaultedIds.slice(0, 3).join(', ');
        notes.push(`Recipes defaulted: ${stats.recipesDefaulted}${named ? ` (no planner match for: ${named})` : ''}.`);
    }
    if(stats.multiOption > 0)
        notes.push(`Multi-option machines: ${stats.multiOption} (the first listed recipe was used).`);
    if(stats.unknownSkipped > 0) {
        const named = unknownNames.slice(0, 3).join(', ');
        notes.push(`Unknown prototypes skipped: ${stats.unknownSkipped}${named ? ` (${named})` : ''}.`);
    }
    if(stats.reflectedPlaced > 0)
        notes.push(`Reflected buildings approximated: ${stats.reflectedPlaced} (mirror ignored, rotation kept).`);

    return {ok: true, savedBlueprint, name, notes, stats};
}

function rectsOverlap(ax: number, ay: number, aw: number, ah: number, bx: number, by: number, bw: number, bh: number): boolean {
    return ax < bx + bw && bx < ax + aw && ay < by + bh && by < ay + ah;
}

function chooseRecipe(
    itemName: string,
    candidates: CoiCatalogRecipe[],
    gameIds: string[],
    catalog: CoiCatalog,
    storedProducts: Set<string>,
    productNames: Set<string>,
    stats: CoiImportStats,
    defaultedIds: string[],
): CoiCatalogRecipe {
    let best: CoiCatalogRecipe | undefined = undefined;
    let bestTier = 0;
    let bestBonus = -Infinity;
    //all (gameId, candidate) pairs achieving the best score, for the
    //multi-option count
    let bestPairs: {gameId: string; candidate: string}[] = [];
    for(const gameId of gameIds) {
        const gameTokens = recipeTokens(gameId);
        for(const candidate of candidates) {
            const tier = matchTier(gameId, candidate.name, itemName);
            if(!tier)
                continue;
            let bonus = 0;
            //material variant: a game-id token that is a catalog
            //product appearing in the candidate's inputs
            if(gameTokens.some((t) => productNames.has(t) && candidate.inputs.includes(t)))
                bonus += 15;
            //stored-products evidence
            const storedHits = candidate.inputs.filter((i) => storedProducts.has(i)).length;
            bonus += Math.min(12, storedHits * 4);
            if(tier > bestTier || (tier == bestTier && bonus > bestBonus)) {
                bestTier = tier;
                bestBonus = bonus;
                best = candidate;
                bestPairs = [{gameId, candidate: candidate.name}];
            } else if(tier == bestTier && bonus == bestBonus) {
                bestPairs.push({gameId, candidate: candidate.name});
            }
        }
    }
    if(!best) {
        stats.recipesDefaulted++;
        for(const gameId of gameIds) {
            if(!defaultedIds.includes(gameId))
                defaultedIds.push(gameId);
        }
        return catalog.defaultRecipeOf(itemName) ?? candidates[0]!;
    }
    if(bestTier == 100)
        stats.recipesExact++;
    else if(bestTier == 90)
        stats.recipesNormalized++;
    else if(bestTier == 85)
        stats.recipesPrefix++;
    else if(bestTier == 70)
        stats.recipesToken++;
    else if(bestTier == 40)
        stats.recipesTokenSubset++;
    const distinctGameIds = new Set(bestPairs.map((p) => p.gameId));
    const distinctCandidates = new Set(bestPairs.map((p) => p.candidate));
    if(distinctGameIds.size > 1 && distinctCandidates.size > 1)
        stats.multiOption++;
    return best;
}
